// Helper function to format dates properly (avoiding timezone issues)
export const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    // Handle both date strings and datetime strings
    if (dateStr.includes('T')) {
      // This is a datetime string, format it normally
      return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } else {
      // This is a date string, parse as local date to avoid timezone issues
      const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }
  } catch {
    return dateStr;
  }
};

// Helper function to get Monday of a given week
export const getMondayOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is Sunday
  return new Date(d.setDate(diff));
};

// Helper function to format week range for display
export const formatWeekRange = (mondayDate) => {
  const friday = new Date(mondayDate);
  friday.setDate(friday.getDate() + 4);
  return `${mondayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

// Helper function to determine request type based on policy_id
export const getRequestType = (req, policies) => {
  const policyId = req.policy_id;
  if (!policyId) return 'PTO'; // Default to PTO for backwards compatibility
  
  const policy = policies.find(p => (p.id || p.name) === policyId);
  if (!policy) return 'PTO'; // Default to PTO if policy not found
  
  return policy.policy_type === 'sick' ? 'Sick' : 'PTO';
};

// Helper function to get full request type name
export const getRequestTypeName = (req, policies) => {
  const policyId = req.policy_id;
  if (!policyId) return 'PTO';
  const policy = policies.find(p => (p.id || p.name) === policyId);
  if (!policy) return 'PTO';
  return policy.policy_type === 'sick' ? 'Sick Time' : 'PTO';
};

// Helper function to get status color
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'approved':
      return { bg: '#dcfce7', color: '#166534' };
    case 'rejected':
    case 'denied':
      return { bg: '#fef2f2', color: '#dc2626' };
    case 'pending':
      return { bg: '#fef3c7', color: '#92400e' };
    default:
      return { bg: '#F0EDE6', color: 'var(--symbolica-black)' };
  }
};

// Helper function to get request type color
export const getRequestTypeColor = (requestType) => {
  return requestType === 'Sick' 
    ? { bg: '#fef3c7', color: '#92400e' }
    : { bg: '#dbeafe', color: '#1e40af' };
};

// Helper function to validate email
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Helper function to get employee display name
export const getEmployeeDisplayName = (employee) => {
  if (!employee) return '—';
  const name = `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
  return name || employee.email || '—';
};

// Helper function to sort array
export const sortArray = (array, field, order, policies = []) => {
  return [...array].sort((a, b) => {
    let valA, valB;
    
    switch (field) {
      case 'employee':
        valA = (a.userEmail || a.user_email || '').toLowerCase();
        valB = (b.userEmail || b.user_email || '').toLowerCase();
        break;
      case 'type':
        valA = getRequestType(a, policies).toLowerCase();
        valB = getRequestType(b, policies).toLowerCase();
        break;
      case 'days':
        valA = parseInt(a.days || 0);
        valB = parseInt(b.days || 0);
        break;
      default:
        valA = (a[field] || '').toString().toLowerCase();
        valB = (b[field] || '').toString().toLowerCase();
    }
    
    if (field === 'days') {
      // Numeric comparison for days
      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    } else {
      // String comparison for other fields
      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    }
  });
}; 